import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Input, Table, Select, Space, Modal, DatePicker, Spin, Tooltip, Tag, Tabs } from "antd";
import { getGridTableHeight, isEmpty, getNowAddMonthDate, getNowAddDaysDate, dateFormatter, dataNewlineProc, dataSingleNewlineProc, dataOrgNewlineProc } from '../Common/util';
import { EditableCell, EditableRow } from '../Common/function';
import SelectBox from '../Common/SelectBox';
import { applyActionSelectOptions, applyTimeSelectOptions, applyTimeDays } from '../Common/options';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// 간단한 마크다운 해석 함수
const parseMarkdown = (text) => {
    if (!text) return '';

    return text
        // \\n을 실제 줄바꿈으로 변환
        .replace(/\\n/g, '\n')
        // **bold** -> <strong>
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // *italic* -> <em>
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // `code` -> <code>
        .replace(/`(.*?)`/g, '<code style="background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px;">$1</code>')
        // ### 제목 -> <h3>
        .replace(/^### (.*$)/gim, '<h3 style="margin: 10px 0 5px 0; color: #1890ff;">$1</h3>')
        // ## 제목 -> <h2>
        .replace(/^## (.*$)/gim, '<h2 style="margin: 12px 0 6px 0; color: #1890ff;">$1</h2>')
        // # 제목 -> <h1>
        .replace(/^# (.*$)/gim, '<h1 style="margin: 15px 0 8px 0; color: #1890ff;">$1</h1>')
        // - 리스트 -> <ul><li>
        .replace(/^- (.*$)/gim, '<li style="margin: 2px 0;">$1</li>')
        // 줄바꿈 처리
        .replace(/\n/g, '<br/>');
};

// 등급에 따른 색상 반환 함수
const getGradeColor = (grade) => {
    if (!grade) return 'default';
    const upperGrade = grade.toString().toUpperCase();
    switch(upperGrade) {
        case 'H': return 'red';
        case 'M': return 'gold';
        case 'L': return 'blue';
        default: return 'default';
    }
};

// 등급 태그 렌더링 함수
const renderGradeTag = (grade) => {
    if (!grade) return '-';
    return (
        <Tag color={getGradeColor(grade)} style={{ fontWeight: 'bold' }}>
            {grade.toString().toUpperCase()}
        </Tag>
    );
};

const { Option } = Select;

/** --------------------------- ----------------------------------------------- **/
const PlanTable = ({ data, columnData, onClickSaveHandler, rowSelection, spinflag }) => {

    // 그룹 헤더 스타일 추가
    const groupHeaderStyle = `
        .ant-table-thead > tr > th.group-header.asset-group,
        .ant-table-thead > tr:first-child > th.group-header.asset-group {
            background-color: #e6f7ff !important;
            border-color: #91d5ff !important;
            font-weight: bold !important;
            color: #0050b3 !important;
            text-align: center !important;
        }
        .ant-table-thead > tr > th.group-header.risk-group,
        .ant-table-thead > tr:first-child > th.group-header.risk-group {
            background-color: #fff2e8 !important;
            border-color: #ffcc99 !important;
            font-weight: bold !important;
            color: #d46b08 !important;
            text-align: center !important;
        }
        .ant-table-thead > tr > th.group-header.classification-group,
        .ant-table-thead > tr:first-child > th.group-header.classification-group {
            background-color: #f6ffed !important;
            border-color: #b7eb8f !important;
            font-weight: bold !important;
            color: #389e0d !important;
            text-align: center !important;
        }
        .ant-table-thead > tr > th.group-header.action-group,
        .ant-table-thead > tr:first-child > th.group-header.action-group {
            background-color: #fff1f0 !important;
            border-color: #ffa39e !important;
            font-weight: bold !important;
            color: #cf1322 !important;
            text-align: center !important;
        }

        /* 하위 헤더들의 배경색도 약간 적용 */
        .ant-table-thead > tr:last-child > th {
            background-color: #fafafa !important;
        }
    `;

    // 스타일 삽입
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = groupHeaderStyle;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    /** --------------------------- ----------------------------------------------- **/
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [onAssetTypeSelectOptions, setOnAssetTypeSelectOptions] = useState([]);

    // 모달 상태
    const [modalVisible, setModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');

    // 사유 모달 관련 state
    const [isRemarkModalVisible, setIsRemarkModalVisible] = useState(false);
    const [currentRemark, setCurrentRemark] = useState('');
    const [currentRecord, setCurrentRecord] = useState(null);
    const [activeTab, setActiveTab] = useState('edit');

    const searchInput = useRef(null);
    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters, confirm, dataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
        confirm();
    };

    // 모달 열기 함수
    const showDetailModal = (title, content, assetName) => {
        setModalTitle(`${title} - ${assetName}`);
        setModalContent(content);
        setModalVisible(true);
    };

    // 사유 모달 열기
    const showRemarkModal = (record) => {
        setCurrentRecord(record);
        setCurrentRemark(record.action_remark || '');
        setActiveTab('edit');
        setIsRemarkModalVisible(true);
    };

    // 사유 모달 저장
    const handleRemarkOk = () => {
        if (currentRecord) {
            const newData = [...dataSource];
            const index = newData.findIndex((item) => item._uniqueId == currentRecord._uniqueId);
            if (index > -1) {
                newData[index] = {
                    ...newData[index],
                    action_remark: currentRemark,
                    operation: 'U',
                    operation_local: 'U'
                };
                setDataSource(newData);
            }
        }
        setIsRemarkModalVisible(false);
        setCurrentRecord(null);
        setCurrentRemark('');
    };

    // 사유 모달 취소
    const handleRemarkCancel = () => {
        setIsRemarkModalVisible(false);
        setCurrentRecord(null);
        setCurrentRemark('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm, dataIndex)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    {/* <Button
          type="link"
          size="small"
          onClick={() => {
            confirm({
              closeDropdown: false,
            });
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
          }}
        >
          Filter
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => {
            close();
          }}
        >
          close
        </Button> */}
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });



    // '자산코드,위험코드' 필터 옵션 생성
    const assetCdFilters = [...new Set(data.map(item => item.asset_cd))].sort()
        .map(code => ({ text: code, value: code }));

    const riskCodeFilters = [...new Set(data.map(item => item.risk_cd))].sort()
        .map(code => ({ text: code, value: code }));
    
    const assetNameFilters = [...new Set(data.map(item => item.asset_nm))].sort()
        .map(code => ({ text: code, value: code }));


    // 위험관리 계획 - 그룹별 헤더로 컬럼 정리
    const planColumnData = [
        // 고정 컬럼 (자산코드)
        {
            title: '자산코드',
            dataIndex: 'asset_cd',
            key: 'asset_cd',
            width: '90px',
            align: 'center',
            fixed: 'left',
            filters: assetCdFilters,
            onFilter: (value, record) => record.asset_cd === value,
        },
        {
            title: '자산명',
            dataIndex: 'asset_nm',
            key: 'asset_nm',
            width: '120px',
            align: 'center',
            filters: assetNameFilters,
            // onFilter: (value, record) => record.risk_cd === value,
            onFilter: (value, record) => {
                return String(record.asset_nm).trim() === String(value).trim();
            },
        },
        {
            title: '위험코드',
            dataIndex: 'risk_cd',
            key: 'risk_cd',
            width: '100px',
            align: 'center',
            filters: riskCodeFilters,
            // onFilter: (value, record) => record.risk_cd === value,
            onFilter: (value, record) => {
                return String(record.risk_cd).trim() === String(value).trim();
            },
        },

        // 1. 자산 정보 그룹
        {
            title: '자산 정보',
            align: 'center',
            className: 'group-header asset-group',
            children: [
                // {
                //     title: '자산명',
                //     dataIndex: 'asset_nm',
                //     key: 'asset_nm',
                //     width: '120px',
                //     align: 'center',
                //     ...getColumnSearchProps('asset_nm'),
                // },
                {
                    title: 'IP',
                    dataIndex: 'asset_ip',
                    key: 'asset_ip',
                    width: '120px',
                    align: 'center',
                    sorter: {
                        compare: (a, b) => a.asset_ip.localeCompare(b.asset_ip),
                    },
                },
                {
                    title: '자산유형',
                    dataIndex: 'asset_spec',
                    key: 'asset_spec',
                    width: '100px',
                    align: 'center',
                    sorter: {
                        compare: (a, b) => a.asset_spec.localeCompare(b.asset_spec),
                    },
                },
                {
                    title: '자산등급',
                    dataIndex: 'asset_grd',
                    key: 'asset_grd',
                    width: '80px',
                    align: 'center',
                    render: (grade) => renderGradeTag(grade),
                    sorter: {
                        compare: (a, b) => (a.asset_grd || '').localeCompare(b.asset_grd || ''),
                    },
                },
            ]
        },

        // 2. 위험 평가 그룹
        {
            title: '위험 평가',
            align: 'center',
            className: 'group-header risk-group',
            children: [
                {
                    title: '위협코드',
                    dataIndex: 'threat_cd',
                    key: 'threat_cd',
                    width: '100px',
                    align: 'center',
                    sorter: {
                        compare: (a, b) => (a.threat_cd || '').localeCompare(b.threat_cd || ''),
                    },
                },
                {
                    title: '위협등급',
                    dataIndex: 'threat_grd',
                    key: 'threat_grd',
                    width: '80px',
                    align: 'center',
                    render: (grade) => renderGradeTag(grade),
                    sorter: {
                        compare: (a, b) => (a.threat_grd || '').localeCompare(b.threat_grd || ''),
                    },
                },
                // {
                //     title: '위험코드',
                //     dataIndex: 'risk_cd',
                //     key: 'risk_cd',
                //     width: '100px',
                //     align: 'center',
                //     filters: riskCodeFilters,
                //     onFilter: (value, record) => record.risk_cd === value,
                //     // onFilter: (value, record) => {
                //     //     // 필터링 될 때마다 콘솔 확인 가능
                //     //     console.log('Filtering:', record.risk_cd, 'vs', value);
                //     //     return String(record.risk_cd).trim() === String(value).trim();
                //     // },
                // },
                {
                    title: '위험도',
                    dataIndex: 'risk_score',
                    key: 'risk_score',
                    width: '70px',
                    align: 'center',
                    sorter: {
                        compare: (a, b) => a.risk_score - b.risk_score,
                    },
                },
                {
                    title: '진단결과',
                    dataIndex: 'result_cd',
                    key: 'result_cd',
                    width: '80px',
                    align: 'center',
                    sorter: {
                        compare: (a, b) => a.result_cd.localeCompare(b.result_cd),
                    },
                },
                {
                    title: '취약점',
                    dataIndex: 'measure_nm',
                    key: 'measure_nm',
                    width: '200px',
                    align: 'center',
                    ellipsis: false,
                    render: (_, rowData) => {
                        return (
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                {rowData.measure_nm}
                            </div>
                        );
                    },
                    sorter: {
                        compare: (a, b) => a.measure_nm.localeCompare(b.measure_nm),
                    },
                    ...getColumnSearchProps('measure_nm'),
                },
                {
                    title: '취약점등급',
                    dataIndex: 'measure_grd',
                    key: 'measure_grd',
                    width: '90px',
                    align: 'center',
                    render: (grade) => renderGradeTag(grade),
                    sorter: {
                        compare: (a, b) => (a.measure_grd || '').localeCompare(b.measure_grd || ''),
                    },
                },
                {
                    title: '현황',
                    dataIndex: 'result_desc',
                    key: 'result_desc',
                    width: '150px',
                    align: 'center',
                    ellipsis: true,
                    render: (value, record) => {
                        const cleanText = value ? value.replace(/[*#`\-]/g, '').trim() : '';
                        const displayText = cleanText ? (cleanText.length > 10 ? cleanText.substring(0, 10) + '...' : cleanText) : '';
                        return (
                            <div
                                style={{
                                    cursor: value ? 'pointer' : 'default',
                                    color: value ? '#1890ff' : 'inherit',
                                    textAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                                onClick={() => value && showDetailModal('현황', value, record.asset_nm)}
                            >
                                <Tooltip title={value ? "클릭하여 전체 내용 보기" : ""}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {displayText}
                                        {value && <InfoCircleOutlined style={{ marginLeft: 4, fontSize: '12px' }} />}
                                    </span>
                                </Tooltip>
                            </div>
                        );
                    },
                },
                {
                    title: '보호대책',
                    dataIndex: 'suggest_desc',
                    key: 'suggest_desc',
                    width: '150px',
                    align: 'center',
                    ellipsis: true,
                    render: (value, record) => {
                        const cleanText = value ? value.replace(/[*#`\-]/g, '').trim() : '';
                        const displayText = cleanText ? (cleanText.length > 10 ? cleanText.substring(0, 10) + '...' : cleanText) : '';
                        return (
                            <div
                                style={{
                                    cursor: value ? 'pointer' : 'default',
                                    color: value ? '#1890ff' : 'inherit',
                                    textAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                                onClick={() => value && showDetailModal('보호대책', value, record.asset_nm)}
                            >
                                <Tooltip title={value ? "클릭하여 전체 내용 보기" : ""}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {displayText}
                                        {value && <InfoCircleOutlined style={{ marginLeft: 4, fontSize: '12px' }} />}
                                    </span>
                                </Tooltip>
                            </div>
                        );
                    },
                },
            ]
        },

        // 3. 위험 분류 그룹 (기존 그대로)
        {
            title: '위험 분류',
            align: 'center',
            className: 'group-header classification-group',
            children: [
                {
                    title: '비용',
                    dataIndex: 'cost_measure',
                    key: 'cost_measure',
                    width: '50px',
                    align: 'center',
                },
                {
                    title: '위험도',
                    dataIndex: 'risk_measure',
                    key: 'risk_measure',
                    width: '60px',
                    align: 'center',
                },
                {
                    title: '난이도',
                    dataIndex: 'difficulty_measure',
                    key: 'difficulty_measure',
                    width: '60px',
                    align: 'center',
                },
            ]
        },

        // 4. 조치 계획 그룹
        {
            title: '조치 계획',
            align: 'center',
            className: 'group-header action-group',
            children: [
                {
                    title: '대응전략',
                    dataIndex: 'apply_action',
                    key: 'apply_action',
                    width: '100px',
                    align: 'center',
                    className: 'edit_col',
                    editable: true,
                    ...getColumnSearchProps('apply_action'),
                },
                {
                    title: '적용시기',
                    dataIndex: 'apply_time',
                    key: 'apply_time',
                    width: '90px',
                    align: 'center',
                    className: 'edit_col',
                    editable: true,
                    ...getColumnSearchProps('apply_time'),
                },
                {
                    title: '조치일정',
                    dataIndex: 'action_time',
                    key: 'action_time',
                    width: '120px',
                    align: 'center',
                    className: 'edit_col',
                    editable: true,
                    ...getColumnSearchProps('action_time'),
                },
                {
                    title: '담당자',
                    dataIndex: 'action_manager',
                    key: 'action_manager',
                    width: '100px',
                    align: 'center',
                    className: 'edit_col',
                    editable: true,
                    ...getColumnSearchProps('action_manager'),
                },
                {
                    title: '사유',
                    dataIndex: 'action_remark',
                    key: 'action_remark',
                    width: '200px',
                    align: 'left',
                    className: 'edit_col',
                    editable: false,
                    ...getColumnSearchProps('action_remark'),
                    onCell: (record) => ({
                        style: {
                            verticalAlign: 'middle',
                            cursor: 'pointer',
                            padding: 0
                        },
                        onClick: () => showRemarkModal(record)
                    }),
                    render: (value, record, index) => {
                        return (
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '8px',
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="클릭하여 수정"
                            >
                                <div style={{ width: '100%', pointerEvents: 'none' }}>
                                    {value ? (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                p: ({node, ...props}) => <p style={{margin: 0, marginBottom: '4px', lineHeight: '1.5'}} {...props} />,
                                                ul: ({node, ...props}) => <ul style={{margin: 0, marginBottom: '4px', paddingLeft: '20px'}} {...props} />,
                                                ol: ({node, ...props}) => <ol style={{margin: 0, marginBottom: '4px', paddingLeft: '20px'}} {...props} />,
                                                h1: ({node, ...props}) => <h1 style={{margin: 0, marginBottom: '4px', fontSize: '1.5em', fontWeight: 'bold'}} {...props} />,
                                                h2: ({node, ...props}) => <h2 style={{margin: 0, marginBottom: '4px', fontSize: '1.3em', fontWeight: 'bold'}} {...props} />,
                                                h3: ({node, ...props}) => <h3 style={{margin: 0, marginBottom: '4px', fontSize: '1.1em', fontWeight: 'bold'}} {...props} />,
                                                h4: ({node, ...props}) => <h4 style={{margin: 0, marginBottom: '4px', fontSize: '1em', fontWeight: 'bold'}} {...props} />,
                                            }}
                                        >
                                            {value}
                                        </ReactMarkdown>
                                    ) : (
                                        <span style={{color: '#999'}}>-</span>
                                    )}
                                </div>
                            </div>
                        );
                    },
                },
            ]
        },
    ];


    //----------------------------------------------------------------------------------------
    // 변수 선언
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState(data);

    // 위험관리계획 일괄수정을 위한 수정값
    const [applyAction, setApplyAction] = useState('');
    const [applyTime, setApplyTime] = useState('');
    const [actionTime, setActionTime] = useState('');
    const [actionManager, setActionManager] = useState('');
    let changeData = [];

    useEffect(() => {
        // 프론트에서만 사용되는 키 값을 삭제
        data.forEach(obj => {
            delete obj.operation;
        });
        
        const processedData = data.map((item, index) => ({
            ...item,
            _uniqueId: `${item.asset_cd}_${item.risk_cd}_${index}` // 고유 ID 추가
        }));

        console.log("spinflag: "+spinflag);
        setDataSource(processedData);
    }, [data]);

    const operationColumns = [
        {
            key: 'operation',
            width: '0px',
            defualt: '',
        },
    ];

    // 미확정상태의 경우 operation칼럼 추가 및 edit기능 추가
    let mergedColumns = [];
    ///const columns = [...columnData];
    const columns = [...planColumnData];
    
    operationColumns.map((col) => {
        columns.push(col);
    });

    mergedColumns = columns.map((col) => {
        if (col.children) {
            // 그룹 헤더가 있는 경우, children을 처리
            return {
                ...col,
                children: col.children.map((childCol) => {
                    if (!childCol.editable) {
                        return childCol;
                    }
                    return {
                        ...childCol,
                        onCell: (record) => ({
                            record,
                            editable: childCol.editable,
                            dataIndex: childCol.dataIndex,
                            title: childCol.title,
                            handleSave : (row) => handleSave(row, childCol.dataIndex),
                        }),
                    };
                })
            };
        }

        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave : (row) => handleSave(row, col.dataIndex),
            }),
        };
    });

    //행 저장
    const handleSave = (row, key) => {

        const newData = [...dataSource];
        // const index = newData.findIndex((item) => row.id == item.id);
        const index = newData.findIndex((item) => row._uniqueId == item._uniqueId);
        const item = newData[index];
        const remarkMessage = "사유를 반드시 적어주세요!";

        if(key == "apply_action" || key == "apply_time"){
            if (row.apply_action  == "위험감소") {
                if(row.apply_time == "장기") {
                    row.action_remark = row.action_remark ? row.action_remark : remarkMessage;
                }else{
                    if(row.action_remark == remarkMessage)  row.action_remark = "";
                }

                if(row.apply_time == "" || row.apply_time == null || row.apply_time == undefined) {
                    row.apply_time = "단기";
                }       
                
                row.action_time = getNowAddDaysDate(applyTimeDays[row.apply_time], '-', row.sys_reg_date);                
            }
        }
        
        if (row.apply_action == "위험수용"  || row.apply_action  == "위험회피" || row.apply_action == "위험이전") {
            row.apply_time = "";
            row.action_time = "";
            row.action_remark = row.action_remark ? row.action_remark : remarkMessage;
        }
   

        // if (row.apply_action == "위험감소" && row.apply_time == "장기") {
        //     row.action_remark = row.action_remark ? row.action_remark : "사유를 반드시 적어주세요!";
        //     row.action_time = getNowAddMonthDate(12, '-');
        //     // row.action_time = row.action_time ? getNowAddMonthDate(12, '-', row.action_time ): getNowAddMonthDate(12, '-');
        // } else if (row.apply_action == "위험수용") {
        //     row.apply_time = "";
        //     row.action_time = "";
        //     row.action_remark = row.action_remark ? row.action_remark : "사유를 반드시 적어주세요!";
        // } else {
        //     const today = new Date();
        //     const year = today.getFullYear();
        //     const month = String(today.getMonth() + 1).padStart(2, '0');
        //     const day = String(today.getDate()).padStart(2, '0');

        //     var dateString = year + '-' + month + '-' + day;
        //     row.apply_time = row.apply_time == "" ? "단기" : row.apply_time;

        //     if(row.apply_time == "단기") {
        //         row.action_time = getNowAddMonthDate(1, '-');
        //     }else if(row.apply_time == "중기") {
        //         row.action_time = getNowAddMonthDate(3, '-');
        //     }else if(row.apply_time == "장기") {    
        //         row.action_time = getNowAddMonthDate(12, '-');
        //     }

        //    if(row.apply_time != "" || row.apply_time != null || row.apply_time != undefined) {
        //         row.action_time = getNowAddDaysDate(applyTimeDays[row.apply_time], '-');
        //     }

        //     row.action_time = row.action_time == "" ? dateString : row.action_time;
        // }
        
        if (JSON.stringify(item) != JSON.stringify(row)) {
            row.operation = row.id == '0' ? 'C' : 'U';
            row.operation_local = 'U';
            newData.splice(index, 1, {
                ...item,
                ...row,
            });
            setDataSource(newData);
        }
    };

    useEffect(() => {
        //저장,수정 대상 추가
        const newData = dataSource.filter((item) => item.operation && item.operation != '');

        changeData = [];
        
        newData.map((record) => {
            if (record.operation && record.operation != '') {
                changeData = changeData.concat(record);
            }
        });

    }, [dataSource]);

    //----------------------------------------------------------------------------------------
    // Date 입력값 변경
    const onChangeActionTime = (formatString) => {
        if (formatString != null) {
            setActionTime(formatString.replace(/[-]/g, ''));
        } else {
            setActionTime('');
        }
    };

    // 위험관리계획 일괄수정
    const onClickApplyHandler = () => {
        if (isEmpty(applyAction) && isEmpty(applyTime) && isEmpty(actionTime) && isEmpty(actionManager)) {
            Modal.info({ title: "적용하고자 하는 항목과 내용를 입력하세요. " });
            return;
        }

        if (isEmpty(rowSelection.selectedRowKeys) || rowSelection.selectedRowKeys.length == 0) {
            Modal.info({ title: "적용하고자 하는 대상을 선택해주세요. " });
            return;
        }

        const newData = [...dataSource];
        newData.map((list) => {
            if (rowSelection.selectedRowKeys.indexOf(list.id) > -1) {
                list.operation = 'U';
                if (applyAction != '') {
                    list.apply_action = applyAction;
                };
                if (applyTime != '') {
                    list.apply_time = applyTime;
                };
                if (actionTime != '') {
                    list.action_time = actionTime;
                };
                if (actionManager != '') {
                    list.action_manager = actionManager;
                };
            }
        })
        setDataSource(newData);
    };
    //----------------------------------------------------------------------------------------
    return (
        // <Spin size={"large"}  tip={"Loading..."} spinning={spinflag}>
        <>
            <table className='search' style={{ borderTopColor: '#dddddd', borderTopStyle: 'solid', borderTopWidth: 'thin' }}>
                <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                </colgroup>
                <tbody>
                    <tr>
                        <td>
                            <div style={{ marginLeft: '20px' }}>총 {dataSource?.length} 개</div>
                        </td>
                        <th>대응전략</th>
                        <td><SelectBox
                            tag='applyAction'
                            options={applyActionSelectOptions}
                            value={applyAction}
                            onChangeHandler={(value) => {
                                setApplyAction(value);
                            }}
                        />
                        </td>
                        <th>적용시기</th>
                        <td><SelectBox
                            tag='applyTime'
                            options={applyTimeSelectOptions}
                            value={applyTime}
                            // onChangeHandler={onChangeApplyTime}
                        />
                        </td>
                        <th>조치일자</th>
                        <td><DatePicker id="actionTime" size="small" allowClear
                            format={'YYYY-MM-DD'}
                            value={actionTime ? dayjs(actionTime, 'YYYYMMDD') : null}
                            onChange={onChangeActionTime} />
                        </td>
                        <th>조치담당자</th>
                        <td><Input
                            id="actionManager"
                            size="small"
                            allowClear
                            value={actionManager}
                            onChange={(event) => {
                                setActionManager(event.target.value);
                            }}
                        />
                        </td>
                        <th>
                            <Button type="default" onClick={onClickApplyHandler}>
                                일괄적용
                            </Button>
                        </th>
                        <td style={{ textAlign: 'right' }}>
                            <Button type="primary" onClick={() => onClickSaveHandler(changeData)}>
                                위험관리계획 저장
                            </Button>
                        </td>
                    </tr></tbody>
            </table>
            <Form form={form} component={false}>
                <Table
                    columns={mergedColumns}
                    dataSource={dataSource}
                    rowKey={(record) => record._uniqueId} 
                    // rowKey={(record) => (`${record.asset_cd}_${record.risk_cd}`)}
                    // rowKey={(record, index) => index}
                    scroll={{ y: getGridTableHeight(455) }}
                    pagination={{ position: ['bottomCenter'], defaultPageSize: [100] }}
                    bordered size={"small"}
                    style={{ height: getGridTableHeight(500) }}
                    showSorterTooltip={false}
                    components={{
                        body: {
                            row: EditableRow,
                            cell: (props) => (
                                <EditableCell 
                                    {...props} 
                                />
                            ),
                        },
                    }}
                    loading={spinflag} 
                    rowClassName={() => 'editable-row'}
                    rowSelection={{ type: 'checkbox', ...rowSelection }}
                />
            </Form>

            {/* 상세 내용 모달 */}
            <Modal
                title={modalTitle}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        닫기
                    </Button>
                ]}
                width={700}
                style={{ top: 50 }}
            >
                <div style={{
                    maxHeight: '500px',
                    overflowY: 'auto',
                    padding: '15px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '6px',
                    lineHeight: '1.8',
                    fontSize: '14px'
                }}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            h1: ({node, ...props}) => <h1 style={{marginTop: '10px', marginBottom: '10px', fontSize: '1.8em', fontWeight: 'bold', color: '#1890ff'}} {...props} />,
                            h2: ({node, ...props}) => <h2 style={{marginTop: '10px', marginBottom: '8px', fontSize: '1.5em', fontWeight: 'bold', color: '#1890ff'}} {...props} />,
                            h3: ({node, ...props}) => <h3 style={{marginTop: '8px', marginBottom: '6px', fontSize: '1.3em', fontWeight: 'bold', color: '#1890ff'}} {...props} />,
                            h4: ({node, ...props}) => <h4 style={{marginTop: '8px', marginBottom: '6px', fontSize: '1.1em', fontWeight: 'bold'}} {...props} />,
                            p: ({node, ...props}) => <p style={{marginBottom: '8px', lineHeight: '1.8'}} {...props} />,
                            ul: ({node, ...props}) => <ul style={{marginBottom: '8px', paddingLeft: '24px', listStyleType: 'disc'}} {...props} />,
                            ol: ({node, ...props}) => <ol style={{marginBottom: '8px', paddingLeft: '24px'}} {...props} />,
                            li: ({node, ...props}) => <li style={{marginBottom: '4px', lineHeight: '1.6'}} {...props} />,
                            code: ({node, inline, ...props}) => inline
                                ? <code style={{backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px', fontSize: '0.9em', fontFamily: 'monospace'}} {...props} />
                                : <code style={{display: 'block', backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '0.9em', fontFamily: 'monospace', overflowX: 'auto'}} {...props} />,
                            strong: ({node, ...props}) => <strong style={{fontWeight: 'bold', color: '#262626'}} {...props} />,
                            em: ({node, ...props}) => <em style={{fontStyle: 'italic'}} {...props} />,
                        }}
                    >
                        {modalContent || '내용이 없습니다.'}
                    </ReactMarkdown>
                </div>
            </Modal>

            {/* 사유 수정 모달 */}
            <Modal
                title={
                    <div style={{
                        paddingRight: '40px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        사유 수정
                    </div>
                }
                open={isRemarkModalVisible}
                onOk={handleRemarkOk}
                onCancel={handleRemarkCancel}
                okText="저장"
                cancelText="취소"
                width={1200}
            >
                <div style={{ display: 'flex', gap: '16px' }}>
                    {/* 왼쪽: 현황 */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <h4 style={{ marginTop: 0, marginBottom: '12px', flexShrink: 0 }}>현황</h4>
                        {currentRecord && (
                            <div style={{
                                height: '600px',
                                padding: '12px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                overflowY: 'auto',
                                flexShrink: 0
                            }}>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: parseMarkdown(currentRecord.result_desc || '현황 정보가 없습니다.')
                                    }}
                                    style={{
                                        fontFamily: 'inherit',
                                        lineHeight: '1.6'
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* 오른쪽: 사유 수정 */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ height: '38px', flexShrink: 0 }}></div>
                        {currentRecord && (
                            <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '12px', flexShrink: 0 }}>
                                <div style={{ marginBottom: '4px', fontSize: '13px' }}>
                                    <strong>자산코드:</strong> {currentRecord.asset_cd}
                                </div>
                                <div style={{ marginBottom: '4px', fontSize: '13px' }}>
                                    <strong>자산명:</strong> {currentRecord.asset_nm}
                                </div>
                                <div style={{ fontSize: '13px' }}>
                                    <strong>위험코드:</strong> {currentRecord.risk_cd}
                                </div>
                            </div>
                        )}
                        <div style={{ height: '526px', display: 'flex', flexDirection: 'column' }}>
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                items={[
                                    {
                                        key: 'edit',
                                        label: '편집',
                                        children: (
                                            <Input.TextArea
                                                value={currentRemark}
                                                onChange={(e) => setCurrentRemark(e.target.value)}
                                                placeholder="사유를 입력하세요 (마크다운 지원)"
                                                style={{
                                                    width: '100%',
                                                    height: '471px',
                                                    fontFamily: 'monospace',
                                                    resize: 'none'
                                                }}
                                            />
                                        )
                                    },
                                    {
                                        key: 'preview',
                                        label: '미리보기',
                                        children: (
                                            <div style={{
                                                height: '492px',
                                                padding: '12px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                backgroundColor: '#fff',
                                                overflowY: 'auto'
                                            }}>
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    h1: ({node, ...props}) => <h1 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.8em', fontWeight: 'bold'}} {...props} />,
                                                    h2: ({node, ...props}) => <h2 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.5em', fontWeight: 'bold'}} {...props} />,
                                                    h3: ({node, ...props}) => <h3 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.3em', fontWeight: 'bold'}} {...props} />,
                                                    h4: ({node, ...props}) => <h4 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.1em', fontWeight: 'bold'}} {...props} />,
                                                    p: ({node, ...props}) => <p style={{marginBottom: '8px', lineHeight: '1.6'}} {...props} />,
                                                    ul: ({node, ...props}) => <ul style={{marginBottom: '8px', paddingLeft: '24px'}} {...props} />,
                                                    ol: ({node, ...props}) => <ol style={{marginBottom: '8px', paddingLeft: '24px'}} {...props} />,
                                                }}
                                            >
                                                {currentRemark || '*미리보기할 내용이 없습니다.*'}
                                            </ReactMarkdown>
                                        </div>
                                    )
                                }
                            ]}
                        />
                        </div>
                    </div>
                </div>
            </Modal>
        {/* </Spin> */}
            </>

    );
}
export default PlanTable;